import { defineStore } from 'pinia'
import { reactive, computed } from 'vue'
import { usePublishedNewsStore } from '@/ContextTab/stores/newsSet/published/published'
import type { NewsSetFilters } from '@/lib/types'
import type { TCodeId, TUserId } from '@/ContextTab/stores/types'
import type {
  PublishedFilterConfiguratorType,
} from '@/ContextTab/modules/news/published/components/types'


export type TIdFilterState = 'included' | 'excluded' | 'ignored'
export type CodeFilterForPredicates = { id: TCodeId, state: TIdFilterState }
export type UserFilterForPredicates = { id: TUserId, state: TIdFilterState }

export const usePublishedFiltersStore = defineStore('publishedFilters', () => {

  const publishedNewsStore = usePublishedNewsStore()

  const filters = reactive<NewsSetFilters>(makeFilters())

  function makeFilters(filter = {}) {
    return {
      code_ids: [],
      code_excluded_ids: [],
      code_ignored_ids: [],
      feed_code_ids: [],
      feed_code_excluded_ids: [],
      feed_code_ignored_ids: [],
      user_ids: [],
      user_excluded_ids: [],
      user_group_ids: [],
      user_group_excluded_ids: [],
      ...filter,
    }
  }

  const codeFiltersForPredicates = computed<CodeFilterForPredicates[]>(() => [
    ...(filters.code_ids || []).map((id) => ({
      id,
      state: 'included' as TIdFilterState,
    })),
    ...(filters.code_excluded_ids || []).map((id) => ({
      id,
      state: 'excluded' as TIdFilterState,
    })),
    ...(filters.code_ignored_ids || []).map((id) => ({
      id,
      state: 'ignored' as TIdFilterState,
    })),
  ])

  const feedCodeFiltersForPredicates = computed<CodeFilterForPredicates[]>(() => [
    ...(filters.feed_code_ids || []).map((id) => ({
      id,
      state: 'included' as TIdFilterState,
    })),
    ...(filters.feed_code_excluded_ids || []).map((id) => ({
      id,
      state: 'excluded' as TIdFilterState,
    })),
    ...(filters.feed_code_ignored_ids || []).map((id) => ({
      id,
      state: 'ignored' as TIdFilterState,
    })),
  ])

  const userFiltersForPredicates = computed<UserFilterForPredicates[]>(() => [
    ...(filters.user_ids || []).map((id) => ({
      id,
      state: 'included' as TIdFilterState,
    })),
    ...(filters.user_excluded_ids || []).map((id) => ({
      id,
      state: 'excluded' as TIdFilterState,
    })),
    ...(filters.user_group_ids || []).map((id) => ({
      id: `${id}G`,
      state: 'included' as TIdFilterState,
    })),
    ...(filters.user_group_excluded_ids || []).map((id) => ({
      id: `${id}G`,
      state: 'excluded' as TIdFilterState,
    })),
  ])

  async function updateFilters(updated: Partial<NewsSetFilters>) {
    Object.keys(updated).forEach((key) => {
      if (filters[key]) {
        filters[key] = updated[key]
      }
    })
    await publishedNewsStore.reload()
  }

  async function resetFilters() {
    await updateFilters(makeFilters())
  }

  function clearFilter(name: PublishedFilterConfiguratorType) {
    switch (name) {
      case 'codes':
        return updateFilters({
          code_ids: [],
          code_excluded_ids: [],
          code_ignored_ids: [],
        })
      case 'feeds':
        return updateFilters({
          feed_code_ids: [],
          feed_code_excluded_ids: [],
          feed_code_ignored_ids: [],
        })
      case 'users':
        return updateFilters({
          user_ids: [],
          user_excluded_ids: [],
          user_group_ids: [],
          user_group_excluded_ids: [],
        })
    }
  }

  return {
    filters,
    codeFiltersForPredicates,
    feedCodeFiltersForPredicates,
    userFiltersForPredicates,

    updateFilters,
    clearFilter,
    resetFilters,
  }
})
